import * as AdminAuditTrailService from "services/AdminAuditTrailService"
import * as AdminPatientAccountService from "services/AdminPatientAccountService"
import * as AdminPracticeAuthorizationService from "services/AdminPracticeAuthorizationService"
import * as AdminPracticeService from "services/AdminPracticeService"
import * as AdminUserService from "services/AdminUserService"
import * as AgendaBlockService from "services/AgendaBlockService"
import * as AgendaService from "services/AgendaService"
import * as AppointmentService from "services/AppointmentService"
import * as AssignService from "services/AssignService"
import * as AuthMfaService from "services/AuthMfaService"
import * as AuthService from "services/AuthService"
import * as CareGroupIllustrationService from "services/CareGroupIllustrationService"
import * as CareGroupLogoService from "services/CareGroupLogoService"
import * as CareGroupService from "services/CareGroupService"
import * as CommunicationLogService from "services/CommunicationLogService"
import * as CommunicationTemplateService from "services/CommunicationTemplateService"
import * as DeploymentDateService from "services/DeploymentDateService"
import * as DigidService from "services/DigidService"
import * as EconsultService from "services/EconsultService"
import { registerErrorCodeHandler } from "services/generic/AxiosInterceptor/errorCodeHandlers"
import * as InboxService from "services/InboxService"
import * as InternalNoteService from "services/InternalNoteService"
import * as LabelService from "services/LabelService"
import * as MaintenanceBannerService from "services/MaintenanceBannerService"
import * as MedicationRequestService from "services/MedicationRequestService"
import * as NotificationService from "services/NotificationService"
import * as OnboardingService from "services/OnboardingService"
import * as PageInvitationService from "services/PageInvitationService"
import * as PageService from "services/PageService"
import * as PatientAccountService from "services/PatientAccountService"
import * as PatientAllergyService from "services/PatientAllergyService"
import * as PatientAppointmentService from "services/PatientAppointmentService"
import * as PatientEconsultService from "services/PatientEconsultService"
import * as PatientEpisodesService from "services/PatientEpisodesService"
import * as PatientMedicalResultsService from "services/PatientMedicalResultsService"
import * as PatientMedicationRequestService from "services/PatientMedicationRequestService"
import * as PatientPlatoService from "services/PatientPlatoService"
import * as PatientsService from "services/PatientsService"
import * as PatientSwapService from "services/PatientSwapService"
import * as PatientWalkInConsultService from "services/PatientWalkInConsultService"
import * as PersonService from "services/PersonService"
import * as PhoneService from "services/PhoneService"
import * as PhoneSettingsService from "services/PhoneSettingsService"
import * as PlatoProService from "services/PlatoProService"
import * as PostalCodeService from "services/PostalCodeService"
import * as PracticeAuthorizationService from "services/PracticeAuthorizationService"
import * as PracticeFeedbackService from "services/PracticeFeedbackService"
import * as PracticeLogoService from "services/PracticeLogoService"
import * as PracticeService from "services/PracticeService"
import * as PublicAgbService from "services/PublicAgbService"
import * as PublicAuthService from "services/PublicAuthService"
import * as PublicEconsultService from "services/PublicEconsultService"
import * as PublicPatientAccountService from "services/PublicPatientAccountService"
import * as PublicPlatoService from "services/PublicPlatoService"
import * as PublicPostalCodeWaitingListService from "services/PublicPostalCodeWaitingListService"
import * as PublicPracticeService from "services/PublicPracticeService"
import * as PublicRemindersService from "services/PublicRemindersService"
import * as PublicUserInvitationService from "services/PublicUserInvitationService"
import * as PublicUserService from "services/PublicUserService"
import * as RegistrationRequestService from "services/RegistrationRequestService"
import * as RegistrationService from "services/RegistrationService"
import * as ResponseTemplateService from "services/ResponseTemplateService"
import * as SettingsService from "services/SettingsService"
import * as UploadService from "services/UploadService"
import * as UserInvitationService from "services/UserInvitationService"
import * as UserService from "services/UserService"
import * as UziCertificateService from "services/UziCertificateService"
import * as WalkInConsultService from "services/WalkInConsultService"

import { ErrorCode } from "@shared/enums/errorCode"

import { buildApiFromServices } from "./builder"

/**
 * Api is a trpc-style api object built from all services that automatically wraps all of our service methods with react-query hooks (and also returns some helpers).
 *
 * This is done to reduce the boilerplate usually associated with react query, while still maintaining full type safety and autocompletion.
 *
 * ## Example usage
 *
 * Let's look at the service located at `frontend/src/services/AdminAuditTrailService.ts`. It exports just a single method:
 *
 * ```ts
 * export const findAll = async (query?: PageRequest): Promise<AuditTrailResponsePageResponse> => { ... }
 * ```
 *
 * Using the api object, we can call react-query methods for this service method like so:
 *
 * ```ts
 * // Note: data is of type AuditTrailResponsePageResponse
 * const { data } = api.adminAuditTrail.findAll.useSuspenseQuery({
 *   limit: 10,
 * })
 * ```
 *
 * Internally this will return a useSuspenseQuery hook with `findAll` passed to `queryFn` and `queryKey: ["adminAuditTrail", "findAll", { limit: 10 }]`.
 *
 * ## Exported methods
 *
 * The following react-query hooks are available for each service method:
 *
 * - `useQuery` - Standard react-query useQuery hook, with support for optional suspense. Use this for methods that fetch data (GET).
 * - `useSuspenseQuery` - React-query's useSuspenseQuery hook. Use this for methods that fetch data (GET) when you want to use suspense and error boundary.
 * - `useMutation` - Standard react-query useMutation hook. Use this for methods that modify data (POST, PUT, DELETE).
 * - `invalidateQueries` - Invalidates queries for this method. (You should almost always use this after a mutation!)
 * - `useIsFetching` - Returns whether the query is currently fetching or not.
 * - `useIsMutating` - Returns whether the mutation is currently running or not.
 * - `removeQueries` - Removes queries for this method from the cache. (Avoid using this unless you really know what you're doing!)
 *
 * ## Adding new services
 *
 * To add a new service to the api object, simply add the service import and add it to the `buildApiFromServices` call below.
 *
 * First import the service:
 *
 * ```ts
 * import * as NewService from "services/NewService"
 * ```
 *
 * Then add it to the `buildApiFromServices` call:
 *
 * ```ts
 * export const api = buildApiFromServices({
 *   ...
 *   newService: NewService,
 * })
 * ```
 *
 * Any methods exported from the service will now be available on the api object under `api.newService`.
 *
 * ## More details
 *
 * @see buildApiFromServices in ./builder.ts for more information
 */
export const api = buildApiFromServices({
  adminAuditTrail: AdminAuditTrailService,
  adminPatientAccount: AdminPatientAccountService,
  adminPracticeAuthorization: AdminPracticeAuthorizationService,
  adminPractice: AdminPracticeService,
  adminUser: AdminUserService,
  agendaBlock: AgendaBlockService,
  agenda: AgendaService,
  appointment: AppointmentService,
  assign: AssignService,
  authMfa: AuthMfaService,
  auth: AuthService,
  careGroup: CareGroupService,
  careGroupIllustration: CareGroupIllustrationService,
  careGroupLogo: CareGroupLogoService,
  communicationLog: CommunicationLogService,
  communicationTemplate: CommunicationTemplateService,
  deploymentDate: DeploymentDateService,
  digid: DigidService,
  econsult: EconsultService,
  inbox: InboxService,
  internalNote: InternalNoteService,
  label: LabelService,
  maintenanceBanner: MaintenanceBannerService,
  medicationRequest: MedicationRequestService,
  notification: NotificationService,
  onboarding: OnboardingService,
  page: PageService,
  pageInvitation: PageInvitationService,
  patientAccount: PatientAccountService,
  patientAllergy: PatientAllergyService,
  patientAppointment: PatientAppointmentService,
  patientEconsult: PatientEconsultService,
  patientEpisodes: PatientEpisodesService,
  patientMedicalResults: PatientMedicalResultsService,
  patientMedicationRequest: PatientMedicationRequestService,
  patientPlato: PatientPlatoService,
  patientWalkInConsult: PatientWalkInConsultService,
  patients: PatientsService,
  patientSwap: PatientSwapService,
  person: PersonService,
  phone: PhoneService,
  phoneSettings: PhoneSettingsService,
  platoPro: PlatoProService,
  postalCode: PostalCodeService,
  practiceAuthorization: PracticeAuthorizationService,
  practice: PracticeService,
  practiceFeedback: PracticeFeedbackService,
  practiceLogo: PracticeLogoService,
  publicAgb: PublicAgbService,
  publicAuth: PublicAuthService,
  publicEconsult: PublicEconsultService,
  publicPatientAccount: PublicPatientAccountService,
  publicPlato: PublicPlatoService,
  publicPostalCodeWaitingList: PublicPostalCodeWaitingListService,
  publicReminders: PublicRemindersService,
  publicPractice: PublicPracticeService,
  publicUser: PublicUserService,
  publicUserInvitation: PublicUserInvitationService,
  registrationRequest: RegistrationRequestService,
  registration: RegistrationService,
  responseTemplate: ResponseTemplateService,
  settings: SettingsService,
  upload: UploadService,
  user: UserService,
  userInvitation: UserInvitationService,
  uziCertificate: UziCertificateService,
  walkInConsult: WalkInConsultService,
})

// Lost practice configuration access (e.g. a time-limited admin claim expired
// while the frontend was open): refresh the settings + practice list so the
// Practice gate, redirects and the admin PracticeAccessContext reflect the
// loss of access. Registered here — not in the interceptor — because the
// handler needs `api`, which the interceptor cannot import without a cycle.
// The in-flight guard keeps repeated denied responses from stacking
// invalidation → refetch cycles while access is still expired; once the
// refresh settles, a later denied response can trigger a fresh one.
let refreshingAccessQueries = false

registerErrorCodeHandler(ErrorCode.PracticeConfigurationAccessDenied, () => {
  if (refreshingAccessQueries) {
    return
  }

  refreshingAccessQueries = true

  void Promise.allSettled([api.settings.get.invalidateQueries(), api.practice.getAll.invalidateQueries()]).then(() => {
    refreshingAccessQueries = false
  })
})
